import { sendToApi } from 'api/client'
import { PaginationParams, UserCard } from 'types'
import { Moment } from 'moment'
import { Step } from 'prosemirror-transform'
import { Schema } from 'prosemirror-model'

export enum SuggestionState {
  Open = 'open',
  Closed = 'closed',
  Cancelled = 'cancelled',
}

export enum SuggestionReviewState {
  Pending = 'pending',
  Rejected = 'rejected',
  Approved = 'approved',
}

export enum SuggestionDirection {
  Inbound = 'inbound',
  Outbound = 'outbound',
}

export interface SuggestionUserCard extends UserCard {
  id: number
}

export interface SuggestionBranch {
  key: string
  url: string
  title: string
  current_version: number
  master_branch_parent_version: number | null
  header_emoji: string | null
}

export interface SuggestionReview {
  id: number
  suggestion_id: number
  state: SuggestionReviewState
  due_date: string
  iteration: number
  reviewer: SuggestionUserCard
  acknowledged_last_transition: boolean
  message: string | null
  suggestion_message: string | null
}

export interface Suggestion {
  id: number
  confirmed_unauth_email: boolean
  guest_suggester: boolean
  state: SuggestionState
  branch: SuggestionBranch
  base_branch: SuggestionBranch
  suggester: SuggestionUserCard
  reviews: SuggestionReview[]
  iteration: number
  changes_count: number
  acknowledged_last_transition: boolean
}

export interface CreateReviewsInput {
  subject: string
  message: string
  includeAllEditors?: boolean
  reviewersIds?: number[]
  dueDate: Moment
}

export interface RejectReviewInput {
  subject: string
  message: string
  dueDate: Moment
}

export interface ApproveReviewInput {
  message: string
  steps?: Step<Schema>[]
  steps_base_branch_version?: number
  client_uuid?: string
}

export const SuggestionsClient = {
  index: async (articleKey: string, options: PaginationParams) => {
    return sendToApi<{ suggestions: Suggestion[] }>(`/articles/${articleKey}/suggestions`, {
      method: 'GET',
      params: options,
    })
  },

  create: async (articleKey: string, from_branch?: string, branch_title?: string) => {
    return sendToApi<{ suggestion: Suggestion }>(`/articles/${articleKey}/suggestions`, {
      method: 'POST',
      body: {
        from_branch,
        branch_title,
      },
    })
  },

  show: async (id: number, userId?: string | null) => {
    return sendToApi<{ suggestion: Suggestion }>(`/suggestions/${id}`, {
      method: 'GET',
      params: {
        user_id: userId,
      },
    })
  },

  cancel: async (id: number, userId?: string | null) => {
    return sendToApi<{ suggestion: Suggestion }>(`/suggestions/${id}`, {
      method: 'DELETE',
      body: {
        user_id: userId,
      },
    })
  },

  createReviews: async (suggestionId: number, input: CreateReviewsInput, userId?: string | null) => {
    const { subject, message, includeAllEditors, reviewersIds, dueDate } = input

    return sendToApi<{ reviews: SuggestionReview[] }>(`/suggestions/${suggestionId}/reviews`, {
      method: 'POST',
      body: {
        subject: subject,
        message: message,
        include_all_editors: includeAllEditors,
        reviewers_ids: reviewersIds,
        due_date: dueDate.format('YYYY-MM-DD'),
        user_id: userId,
      },
    })
  },

  rejectReview: async (reviewId: number, input: RejectReviewInput) => {
    const { subject, message, dueDate } = input

    return sendToApi<{ review: SuggestionReview }>(`/suggestion_reviews/${reviewId}/reject`, {
      method: 'POST',
      body: {
        subject: subject,
        message: message,
        due_date: dueDate.format('YYYY-MM-DD'),
      },
    })
  },

  approveReview: async (reviewId: number, input: ApproveReviewInput) => {
    const { message, steps, steps_base_branch_version, client_uuid } = input

    return sendToApi<{ review: SuggestionReview }>(`/suggestion_reviews/${reviewId}/approve`, {
      method: 'POST',
      body: {
        message: message,
        steps: steps,
        steps_base_branch_version: steps_base_branch_version,
        client_uuid: client_uuid,
      },
    })
  },

  createReminder: async (suggestionId: number, unauthUserId?: string | null) => {
    return sendToApi<{}>(`/suggestions/${suggestionId}/reminders`, {
      method: 'POST',
      body: {
        user_id: unauthUserId,
      },
    })
  },

  createShellUserAndBranch: async (articleKey: string, firstName: string, lastName: string, email: string) => {
    return sendToApi<{ suggestion: Suggestion }>(`/articles/${articleKey}/suggestions/create_user_and_suggestion`, {
      method: 'POST',
      body: {
        first_name: firstName,
        last_name: lastName,
        email: email,
      },
    })
  },

  sendConfirmUnauthEmail: (userId: number, suggestionId: number) => {
    return sendToApi(`/suggestions/${suggestionId}/send_confirm_unauthed_user_email`, {
      method: 'POST',
      params: { user_id: userId },
    })
  },

  confirmUnauthEmail: (suggestionId: number, token: string) => {
    return sendToApi<{ suggestion: Suggestion }>(`/suggestions/${suggestionId}/confirm_unauthed_user`, {
      method: 'POST',
      params: { token },
    })
  },
}
